import { Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    generateUniqueStructureNameFromList,
    STRUCTURE_NAME_MAX_LENGTH,
} from '~/domain/services/dicomProcessor';
import { cmToMm } from '~/utils/units';
import {
    ActionButtons,
    LabeledSelect,
    OutputStructureSelect,
    getCustomNameError,
} from '../margin/MarginToolComponents';
import { getStructureColor } from '../margin/marginToolbarUtils';
import { useRingPreview } from './useRingPreview';
import { useRingApplication } from './useRingApplication';
import RingRangeControls from './RingRangeControls';
import OperationContentShell, {
    OperationShellRenderProps,
} from '../shared/OperationContentShell';

const RING_SUFFIX = '_ring';
const DEFAULT_INNER_CM = -0.5;
const DEFAULT_OUTER_CM = 0.5;
const AUTO_PREVIEW_DELAY_MS = 500;

function getDefaultRingName(inputName: string): string {
    const maxBase = STRUCTURE_NAME_MAX_LENGTH - RING_SUFFIX.length;
    return `${inputName.substring(0, maxBase)}${RING_SUFFIX}`;
}

interface RingOperationContentProps {
    onClose: () => void;
    onPreventCloseChange: (prevented: boolean) => void;
}

export default function RingOperationContent({
    onClose,
    onPreventCloseChange,
}: RingOperationContentProps) {
    return (
        <OperationContentShell>
            {(shellProps) => (
                <RingFormContent
                    onClose={onClose}
                    onPreventCloseChange={onPreventCloseChange}
                    {...shellProps}
                />
            )}
        </OperationContentShell>
    );
}

// Inner component that has access to a valid inputRoi —
// hooks need stable references and can't be called conditionally.
interface RingFormContentProps extends OperationShellRenderProps {
    onClose: () => void;
    onPreventCloseChange: (prevented: boolean) => void;
}

function RingFormContent({
    onClose,
    onPreventCloseChange,
    environment,
    inputRoi,
    structures,
    onInputROIChange,
}: RingFormContentProps) {
    // Form state — inner/outer boundaries in cm, converted to mm for hooks
    const [innerCm, setInnerCm] = useState(DEFAULT_INNER_CM);
    const [outerCm, setOuterCm] = useState(DEFAULT_OUTER_CM);
    const [outputRoiNumber, setOutputRoiNumber] = useState<number | null>(null);
    const [customRoiName, setCustomRoiName] = useState('');

    // Preview and application hooks.
    // Destructure the stable useCallback references so downstream deps
    // don't re-fire on every isPreviewing state change.
    const {
        isPreviewing,
        handlePreviewRing,
        consumeRingPreview,
        abortInFlight: abortPreviewInFlight,
        cleanup: previewCleanup,
        updateView,
    } = useRingPreview({ environment, inputRoi });

    const cleanupRef = useRef(previewCleanup);
    cleanupRef.current = previewCleanup;

    const handleClose = useCallback(() => {
        previewCleanup();
        onClose();
    }, [previewCleanup, onClose]);

    const apply = useRingApplication({
        environment,
        inputRoi,
        onSuccess: handleClose,
        updateView,
        consumeRingPreview,
    });

    // Flow isPreviewing state to parent for close-blocking
    const isPreventing = isPreviewing || apply.isProcessing;
    useEffect(() => {
        onPreventCloseChange(isPreventing);
    }, [isPreventing, onPreventCloseChange]);

    // Cleanup on unmount
    useEffect(() => () => cleanupRef.current(), []);

    const inputColor = useMemo(
        () =>
            getStructureColor(
                structures.find((s) => s.name === inputRoi.name)?.color || {
                    R: Math.round(inputRoi.color[0] * 255),
                    G: Math.round(inputRoi.color[1] * 255),
                    B: Math.round(inputRoi.color[2] * 255),
                }
            ),
        [structures, inputRoi.name, inputRoi.color]
    );

    const outputColor = useMemo(
        () =>
            outputRoiNumber === null
                ? undefined
                : getStructureColor(
                      structures.find((s) => s.roiNumber === outputRoiNumber)
                          ?.color
                  ),
        [structures, outputRoiNumber]
    );

    const customNameError = useMemo(
        () =>
            getCustomNameError(
                customRoiName,
                structures.map((s) => s.name)
            ),
        [customRoiName, structures]
    );

    const structureOptions = useMemo(
        () =>
            structures.map((s) => ({
                roiNumber: s.roiNumber,
                label: s.name,
                color: getStructureColor(s.color),
            })),
        [structures]
    );

    const existingNames = useMemo(
        () => structures.map((s) => s.name),
        [structures]
    );

    const defaultRingName = useMemo(
        () =>
            generateUniqueStructureNameFromList(
                existingNames,
                getDefaultRingName(inputRoi.name)
            ),
        [existingNames, inputRoi.name]
    );

    // Ring is invalid when outer <= inner (zero-width or inverted)
    const isInvalid = outerCm <= innerCm;

    const handleApply = useCallback(() => {
        const newName =
            outputRoiNumber === null
                ? generateUniqueStructureNameFromList(
                      existingNames,
                      customRoiName.trim() || getDefaultRingName(inputRoi.name)
                  )
                : undefined;
        apply.handleApplyRing(
            cmToMm(innerCm),
            cmToMm(outerCm),
            outputRoiNumber,
            newName
        );
    }, [
        outputRoiNumber,
        customRoiName,
        inputRoi.name,
        existingNames,
        innerCm,
        outerCm,
        apply,
    ]);

    // Auto-trigger preview with debounce when ring values change.
    //
    // Skipped while apply is in progress: during commit the panel's
    // `inputRoi` can flip transiently (e.g. selectedROIState moving to a
    // freshly-created output ROI), and we don't want that transient state
    // to drive preview lifecycle decisions and destroy the result we're
    // about to apply.
    useEffect(() => {
        if (apply.isProcessing) {
            // Abort any in-flight preview compute so its result doesn't
            // land on the slot mid-apply. Held results stay so apply
            // can still consume them.
            abortPreviewInFlight();
            return;
        }

        // Drop the currently-displayed preview immediately (and abort any
        // in-flight compute) so a stale result can't land on the display
        // during the debounce window before the new compute starts.
        previewCleanup();

        if (isInvalid || inputRoi.isEmpty) {
            return;
        }

        const timeout = setTimeout(() => {
            handlePreviewRing(cmToMm(innerCm), cmToMm(outerCm));
        }, AUTO_PREVIEW_DELAY_MS);

        return () => {
            clearTimeout(timeout);
        };
    }, [
        innerCm,
        outerCm,
        inputRoi,
        isInvalid,
        handlePreviewRing,
        previewCleanup,
        abortPreviewInFlight,
        apply.isProcessing,
    ]);

    const handleSliderChange = useCallback(
        (_: Event, value: number | number[]) => {
            const [newInner, newOuter] = value as number[];
            // disableSwap on the slider prevents thumbs from crossing,
            // so outer > inner is always guaranteed here.
            setInnerCm(newInner);
            setOuterCm(newOuter);
        },
        []
    );

    // customNameError only blocks Apply when the user is creating a new
    // structure (outputRoiNumber === null). Switching to an existing output
    // while a stale custom name is in the text box must not keep Apply disabled.
    const isCustomNameInvalid = outputRoiNumber === null && !!customNameError;

    return (
        <>
            <LabeledSelect
                label="Input Structure"
                value={inputRoi.roiNumber}
                onChange={onInputROIChange}
                options={structureOptions}
                colorDot={inputColor}
            />

            <RingRangeControls
                innerCm={innerCm}
                outerCm={outerCm}
                onInnerChange={setInnerCm}
                onOuterChange={setOuterCm}
                onSliderChange={handleSliderChange}
            />

            <OutputStructureSelect
                outputRoiNumber={outputRoiNumber}
                setOutputRoiNumber={setOutputRoiNumber}
                outputColor={outputColor}
                inputColor={inputColor}
                inputRoiNumber={inputRoi.roiNumber}
                availableStructures={structures}
                customRoiName={customRoiName}
                setCustomRoiName={setCustomRoiName}
                customNameError={customNameError}
                defaultName={defaultRingName}
            />

            {isInvalid && (
                <Typography fontSize={10} color="error" fontStyle="italic">
                    Outer boundary must be greater than inner boundary — a
                    zero-width or inverted ring is not valid.
                </Typography>
            )}

            {inputRoi.isEmpty && (
                <Typography
                    fontSize={10}
                    color="warning.main"
                    fontStyle="italic"
                >
                    Selected input structure is empty. Ring cannot be applied.
                </Typography>
            )}

            <ActionButtons
                onApply={handleApply}
                isApplyDisabled={
                    isInvalid || isCustomNameInvalid || inputRoi.isEmpty
                }
            />
        </>
    );
}
